import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, MinusCircle, Camera, ChevronLeft, ChevronDown, Loader2,
  ShieldCheck, AlertCircle, AlertTriangle, Image as ImageIcon, Ban, MapPin, X,
} from "lucide-react";

const STATUS_LABEL = {
  pending: "Pendiente", in_progress: "En progreso", completed: "Completado",
  completed_with_observations: "Con observaciones", abandoned: "Abandonado", expired: "Expirado",
};
const STATUS_COLOR = {
  pending: "bg-slate-100 text-slate-600",
  in_progress: "bg-yellow-100 text-yellow-700", completed: "bg-green-100 text-green-800",
  completed_with_observations: "bg-orange-100 text-orange-700",
  abandoned: "bg-red-100 text-red-700", expired: "bg-slate-200 text-slate-600",
};
const TURN_LABEL = { apertura: "Apertura", intermedio: "Producción", cierre: "Cierre" };
const API_URL = process.env.REACT_APP_BACKEND_URL;

function getGeoLocation(timeoutMs = 6000) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) { resolve(null); return; }
    const timer = setTimeout(() => resolve(null), timeoutMs);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(timer);
        resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
      },
      () => { clearTimeout(timer); resolve(null); },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 30000 }
    );
  });
}

function compressImage(file, maxSide = 1280) {
  return new Promise((resolve) => {
    const img = document.createElement("img");
    const reader = new FileReader();
    reader.onload = (e) => {
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let { width, height } = img;
        if (width > maxSide || height > maxSide) {
          if (width > height) { height = Math.round((height * maxSide) / width); width = maxSide; }
          else { width = Math.round((width * maxSide) / height); height = maxSide; }
        }
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.8));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

function ValueButtons({ value, disabled, onPick, testidPrefix }) {
  const opts = [
    { v: "yes", label: "Sí", icon: CheckCircle2, active: "bg-green-100 border-green-400 text-green-700" },
    { v: "no", label: "No", icon: XCircle, active: "bg-red-100 border-red-400 text-red-700" },
    { v: "na", label: "N/A", icon: MinusCircle, active: "bg-slate-200 border-slate-400 text-slate-600" },
  ];
  return (
    <div className="flex gap-1.5">
      {opts.map((o) => (
        <button
          key={o.v} disabled={disabled} onClick={() => onPick(o.v)}
          data-testid={`${testidPrefix}-${o.v}`}
          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs font-semibold transition-colors disabled:opacity-50 ${
            value === o.v ? o.active : "border-slate-200 text-slate-500 hover:bg-slate-50"
          }`}
        >
          <o.icon className="w-3.5 h-3.5" /> {o.label}
        </button>
      ))}
    </div>
  );
}

function EvidenceThumb({ path, geo, onClick, onDelete, disabled }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let active = true;
    const token = localStorage.getItem("chizy_token");
    fetch(`${API_URL}/api/ops/photos/${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => { if (active) setSrc(URL.createObjectURL(b)); }).catch(() => {});
    return () => { active = false; };
  }, [path]);
  const hasGeo = geo && geo.lat != null && geo.lng != null;
  return (
    <div className="relative h-14 w-14 flex-shrink-0" data-testid="evidence-thumb">
      {!src ? (
        <div className="h-14 w-14 rounded-lg bg-slate-100 animate-pulse" />
      ) : (
        <img src={src} alt="evidencia" onClick={() => onClick(src)} className="h-14 w-14 rounded-lg object-cover border border-slate-200 cursor-pointer" />
      )}
      {hasGeo && (
        <a
          href={`https://www.google.com/maps?q=${geo.lat},${geo.lng}`}
          target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
          title={`Ver ubicación (±${Math.round(geo.accuracy || 0)}m)`}
          data-testid="evidence-geo-link"
          className="absolute -bottom-1 -left-1 bg-emerald-600 text-white rounded-full p-0.5 shadow"
        >
          <MapPin className="w-2.5 h-2.5" />
        </a>
      )}
      {onDelete && !disabled && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(path); }}
          data-testid="evidence-delete-btn"
          className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full p-0.5 shadow hover:bg-red-700"
        >
          <X className="w-2.5 h-2.5" />
        </button>
      )}
    </div>
  );
}

export default function OpsChecklistDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [abandoning, setAbandoning] = useState(false);
  const [abandonReason, setAbandonReason] = useState("");
  const [showAbandonModal, setShowAbandonModal] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [previewSrc, setPreviewSrc] = useState(null);
  const fileRefs = useRef({});

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/ops/checklists/${id}`);
      setRun(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cargar el checklist");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const respKey = (code, col) => (col ? `${code}__${col}` : code);

  const patchItem = async (item, value, column, extra = {}) => {
    const key = respKey(item.code, column);
    setBusyKey(key);
    try {
      const res = await api.patch(`/ops/checklists/${id}/items/${item.code}`, { value, column, ...extra });
      setRun(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar el ítem");
    } finally {
      setBusyKey(null);
    }
  };

  const handlePhoto = async (item, column, files) => {
    const fileArr = Array.from(files || []);
    if (fileArr.length === 0) return;
    const key = respKey(item.code, column);
    setBusyKey(key);
    try {
      const dataUrls = await Promise.all(fileArr.map((f) => compressImage(f)));
      const existingValue = run.responses?.[key]?.value || "yes";
      const geo = await getGeoLocation();
      const payload = { value: existingValue, column };
      if (dataUrls.length > 1) payload.evidence_list = dataUrls;
      else payload.evidence_base64 = dataUrls[0];
      if (geo) { payload.lat = geo.lat; payload.lng = geo.lng; payload.accuracy = geo.accuracy; }
      const res = await api.patch(`/ops/checklists/${id}/items/${item.code}`, payload);
      setRun(res.data);
      toast.success(geo ? "Evidencia subida con ubicación" : "Evidencia subida (sin ubicación)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al subir la foto");
    } finally {
      setBusyKey(null);
    }
  };

  const deleteEvidence = async (item, column, path) => {
    const key = respKey(item.code, column);
    setBusyKey(key);
    try {
      const res = await api.delete(`/ops/checklists/${id}/items/${item.code}/evidence`, { data: { column, path } });
      setRun(res.data);
      toast.success("Foto eliminada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo borrar la foto");
    } finally {
      setBusyKey(null);
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await api.post(`/ops/checklists/${id}/complete`);
      toast.success("Checklist completado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Faltan ítems por completar");
    } finally {
      setCompleting(false);
    }
  };

  const handleAbandon = async () => {
    if (abandonReason.trim().length < 5) { toast.error("Escribe un motivo de al menos 5 caracteres"); return; }
    setAbandoning(true);
    try {
      await api.post(`/ops/checklists/${id}/abandon`, { reason: abandonReason });
      toast.success("Checklist marcado como abandonado");
      setShowAbandonModal(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al abandonar");
    } finally {
      setAbandoning(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="w-8 h-8 animate-spin text-[#FFDF00]" /></div>;
  if (!run) return null;

  const canManage = ["corporate_admin", "branch_manager", "operativo"].includes(user?.role);
  const isLocked = run.status === "completed" || run.status === "completed_with_observations" || run.status === "abandoned";
  const pct = run.summary?.percentage_complete ?? 0;
  const responses = run.responses || {};

  return (
    <div className="max-w-lg mx-auto px-4 pb-32" data-testid="checklist-detail-page">
      <div className="sticky top-0 bg-white z-10 pt-4 pb-3 border-b border-slate-100 mb-4">
        <button onClick={() => navigate("/ops/checklists")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-2 transition-colors" data-testid="checklist-back-btn">
          <ChevronLeft className="w-4 h-4" /> Volver
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-slate-900">{run.template_name}</h1>
            <div className="text-xs text-slate-500">
              {run.branch_name} · {run.scheduled_date} {run.turn ? `· ${TURN_LABEL[run.turn] || run.turn}` : ""}
            </div>
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${STATUS_COLOR[run.status]}`} data-testid="checklist-status-badge">
            {STATUS_LABEL[run.status]}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <div className="flex-1 bg-slate-100 rounded-full h-2"><div className="bg-[#FFDF00] h-2 rounded-full transition-all" style={{ width: `${pct}%` }} /></div>
          <span className="text-xs font-semibold text-slate-600">{pct.toFixed(0)}%</span>
        </div>
        {run.abandon_reason && <div className="text-xs text-red-600 mt-2">Abandonado: {run.abandon_reason}</div>}
      </div>

      <div className="space-y-4">
        {run.sections.map((sec) => {
          const isOpen = !collapsed[sec.code];
          const secDone = sec.items.reduce((acc, it) => {
            if (it.type === "check_dual_column") {
              return acc + (it.columns || []).filter((c) => responses[respKey(it.code, c.code)]).length;
            }
            return acc + (responses[it.code] ? 1 : 0);
          }, 0);
          const secTotal = sec.items.reduce((acc, it) => acc + (it.type === "check_dual_column" ? (it.columns || []).length : 1), 0);
          return (
            <div key={sec.code} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`checklist-section-${sec.code}`}>
              <button
                onClick={() => setCollapsed((c) => ({ ...c, [sec.code]: !c[sec.code] }))}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
              >
                <span className="font-semibold text-sm text-slate-900">{sec.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">{secDone}/{secTotal}</span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </div>
              </button>
              {isOpen && (
                <div className="px-4 pb-3 space-y-3 border-t border-slate-100 pt-3">
                  {sec.items.map((item) => (
                    <ItemBlock
                      key={item.code} item={item} responses={responses} disabled={isLocked || busyKey || !canManage}
                      busyKey={busyKey} fileRefs={fileRefs} onPatch={patchItem} onPhoto={handlePhoto}
                      onDeleteEvidence={deleteEvidence} onPreview={setPreviewSrc}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {canManage && !isLocked && (
        <div className="fixed bottom-6 left-4 right-20 max-w-lg mx-auto flex gap-2">
          <button
            onClick={() => setShowAbandonModal(true)}
            className="flex items-center justify-center gap-1.5 px-4 py-4 rounded-2xl border border-red-200 text-red-600 font-semibold text-sm hover:bg-red-50 transition-colors"
            data-testid="abandon-checklist-btn"
          >
            <Ban className="w-4 h-4" />
          </button>
          <button
            onClick={handleComplete} disabled={completing}
            className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl bg-slate-900 text-white font-bold text-base shadow-lg hover:bg-slate-800 transition-colors disabled:opacity-60"
            data-testid="complete-checklist-btn"
          >
            {completing ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />}
            {completing ? "Guardando..." : "Marcar como completado"}
          </button>
        </div>
      )}

      {pct < 100 && !isLocked && (
        <div className="mt-6 flex items-center gap-2 text-sm text-slate-500 bg-slate-50 rounded-xl p-3">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {run.summary?.pending || 0} ítems pendientes
        </div>
      )}

      {showAbandonModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="abandon-modal">
          <div className="bg-white rounded-2xl p-5 max-w-sm w-full space-y-3">
            <h3 className="font-bold text-slate-900">Abandonar checklist</h3>
            <textarea
              value={abandonReason} onChange={(e) => setAbandonReason(e.target.value)}
              placeholder="Motivo (mínimo 5 caracteres)..."
              className="w-full border border-slate-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
              rows={3} data-testid="abandon-reason-input"
            />
            <div className="flex gap-2">
              <button onClick={() => setShowAbandonModal(false)} className="flex-1 py-2 rounded-lg border border-slate-200 text-sm font-medium">Cancelar</button>
              <button onClick={handleAbandon} disabled={abandoning} className="flex-1 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold disabled:opacity-60" data-testid="abandon-confirm-btn">
                {abandoning ? "..." : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewSrc && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setPreviewSrc(null)}>
          <img src={previewSrc} alt="evidencia ampliada" className="max-h-[85vh] max-w-full rounded-lg" />
        </div>
      )}
    </div>
  );
}

function ItemBlock({ item, responses, disabled, busyKey, fileRefs, onPatch, onPhoto, onDeleteEvidence, onPreview }) {
  const [drafts, setDrafts] = useState({}); // { [key]: { note, dataUrl } } — borrador local para "No" con evidencia/nota obligatoria

  const renderEvidenceBtn = (col) => {
    const key = col ? `${item.code}__${col}` : item.code;
    const maxPhotos = item.max_photos || 5;
    return (
      <>
        <input
          type="file" accept="image/*" capture="environment" multiple className="hidden"
          ref={(el) => (fileRefs.current[key] = el)}
          onChange={(e) => { onPhoto(item, col, e.target.files); e.target.value = ""; }}
        />
        <button
          disabled={disabled} onClick={() => fileRefs.current[key]?.click()}
          data-testid={`item-photo-btn-${key}`}
          title={`Máx. ${maxPhotos} fotos`}
          className="flex items-center gap-1 px-2 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-500 hover:bg-slate-50 disabled:opacity-50"
        >
          {busyKey === key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
        </button>
      </>
    );
  };

  const draftFileRefs = { current: {} };
  const attachDraftPhoto = async (key, file) => {
    if (!file) return;
    const dataUrl = await compressImage(file);
    setDrafts((d) => ({ ...d, [key]: { ...(d[key] || { note: "" }), dataUrl } }));
  };
  const confirmNoDraft = (item, col) => {
    const key = col ? `${item.code}__${col}` : item.code;
    const d = drafts[key];
    if (!d?.note?.trim()) { toast.error("Escribe una nota explicando la no conformidad"); return; }
    if (!d?.dataUrl) { toast.error("Adjunta una foto de evidencia"); return; }
    onPatch(item, "no", col, { note: d.note.trim(), evidence_base64: d.dataUrl });
    setDrafts((d2) => { const cp = { ...d2 }; delete cp[key]; return cp; });
  };
  const cancelNoDraft = (key) => setDrafts((d) => { const cp = { ...d }; delete cp[key]; return cp; });

  const renderRow = (col, label) => {
    const key = col ? `${item.code}__${col}` : item.code;
    const resp = responses[key];
    const requiresProof = item.force_evidence_on_no;
    const alreadyConfirmedNo = resp?.value === "no" && resp?.note && resp?.evidence_urls?.length > 0;
    const draft = drafts[key];
    return (
      <div key={key} className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          {label && <span className="text-xs font-semibold text-slate-500 w-16">{label}</span>}
          <ValueButtons
            value={resp?.value} disabled={disabled}
            onPick={(v) => {
              if (v === "no" && requiresProof && !alreadyConfirmedNo) {
                setDrafts((d) => ({ ...d, [key]: d[key] || { note: "", dataUrl: null } }));
                return;
              }
              cancelNoDraft(key);
              onPatch(item, v, col, resp?.note ? { note: resp.note } : {});
            }}
            testidPrefix={`item-${key}`}
          />
          {!requiresProof && (item.evidence_optional || item.evidence_required) && renderEvidenceBtn(col)}
        </div>
        {resp?.evidence_urls?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-1" data-testid={`item-evidence-list-${key}`}>
            {resp.evidence_urls.map((path) => (
              <EvidenceThumb
                key={path} path={path} geo={resp.evidence_geo?.[path]} onClick={onPreview}
                disabled={disabled} onDelete={disabled ? null : (p) => onDeleteEvidence(item, col, p)}
              />
            ))}
          </div>
        )}
        {draft && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-2 space-y-1.5" data-testid={`item-no-draft-${key}`}>
            <textarea
              value={draft.note} onChange={(e) => setDrafts((d) => ({ ...d, [key]: { ...d[key], note: e.target.value } }))}
              placeholder="Nota de no conformidad (obligatoria)..." rows={2}
              className="w-full border border-red-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-red-300"
              data-testid={`item-note-${key}`}
            />
            <div className="flex items-center gap-2">
              <input
                type="file" accept="image/*" capture="environment" className="hidden"
                ref={(el) => (draftFileRefs.current[key] = el)}
                onChange={(e) => attachDraftPhoto(key, e.target.files?.[0])}
              />
              <button onClick={() => draftFileRefs.current[key]?.click()} data-testid={`item-no-draft-photo-${key}`} className="flex items-center gap-1 px-2 py-1 rounded-lg border border-red-200 text-xs text-red-600 bg-white">
                <Camera className="w-3.5 h-3.5" /> {draft.dataUrl ? "Foto lista" : "Adjuntar foto (obligatoria)"}
              </button>
              {draft.dataUrl && <img src={draft.dataUrl} alt="evidencia" className="h-8 w-8 rounded object-cover" />}
              <button onClick={() => cancelNoDraft(key)} className="text-xs text-slate-400 ml-auto">Cancelar</button>
              <button
                onClick={() => confirmNoDraft(item, col)} disabled={busyKey === key}
                data-testid={`item-no-draft-confirm-${key}`}
                className="px-2.5 py-1 rounded-lg bg-red-600 text-white text-xs font-semibold disabled:opacity-50"
              >
                Guardar "No"
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (item.type === "check_dual_column") {
    return (
      <div className="border-b border-slate-50 pb-2 last:border-0" data-testid={`checklist-item-${item.code}`}>
        <div className="text-sm text-slate-900 mb-1.5">{item.label}</div>
        <div className="space-y-1.5">{(item.columns || []).map((c) => renderRow(c.code, c.code))}</div>
      </div>
    );
  }

  if (item.type === "number" || item.type === "temperature") {
    const resp = responses[item.code];
    return (
      <div className="flex items-center justify-between gap-2" data-testid={`checklist-item-${item.code}`}>
        <span className="text-sm text-slate-900 flex-1">{item.label}</span>
        <div className="flex items-center gap-1">
          <input
            type="number" step="0.1" defaultValue={resp?.value ?? ""} disabled={disabled}
            placeholder={item.unit || ""}
            onBlur={(e) => e.target.value !== "" && onPatch(item, e.target.value, null)}
            className={`w-20 border rounded-lg px-2 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#FFDF00] ${resp?.alert ? "border-red-400 bg-red-50" : "border-slate-200"}`}
            data-testid={`item-input-${item.code}`}
          />
          {item.unit && <span className="text-xs text-slate-400">{item.unit}</span>}
          {resp?.alert && <AlertTriangle className="w-4 h-4 text-red-500" title="Fuera de rango" />}
        </div>
      </div>
    );
  }

  if (item.type === "text") {
    const resp = responses[item.code];
    return (
      <div className="space-y-1" data-testid={`checklist-item-${item.code}`}>
        <span className="text-sm text-slate-900">{item.label}</span>
        <input
          type="text" defaultValue={resp?.value ?? ""} disabled={disabled}
          onBlur={(e) => e.target.value.trim() !== "" && onPatch(item, e.target.value, null)}
          className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
          data-testid={`item-input-${item.code}`}
        />
      </div>
    );
  }

  // check / check_evidence
  return (
    <div className="space-y-1" data-testid={`checklist-item-${item.code}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <span className="text-sm text-slate-900 flex-1">
          {item.label}
          {item.needs_validation && <span className="ml-1.5 text-[10px] uppercase font-bold text-orange-500">Revisar</span>}
        </span>
        {renderRow(null, null)}
      </div>
      {item.evidence_required && <div className="text-[11px] text-orange-600 flex items-center gap-1"><Camera className="w-3 h-3" /> Foto obligatoria si marcas Sí</div>}
    </div>
  );
}
